import { useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Upload, FileText, Download, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"

interface CSVUploaderProps {
  onFileSelect: (file: File) => void
}

export function CSVUploader({ onFileSelect }: CSVUploaderProps) {
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type === 'text/csv') {
      onFileSelect(file)
    }
  }, [onFileSelect])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
  }

  const downloadTemplate = () => {
    const template = 'Email,First Name,Last Name,Middle Name,Mobile Number,Work Phone\nexample@email.com,John,Doe,Michael,555-1234,555-5678'
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'user-import-template.csv'
    a.click()
  }

  return (
    <div className="space-y-4">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Required columns:</strong> Email, First Name, Last Name
          <br />
          <strong>Optional columns:</strong> Middle Name, Mobile Number, Work Phone
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Upload CSV File</CardTitle>
          <CardDescription>
            Select or drag and drop your CSV file containing user data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary transition-colors cursor-pointer"
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">Drop your CSV file here</p>
            <p className="text-muted-foreground mb-4">or</p>
            <label>
              <Button variant="outline" asChild>
                <span>
                  <FileText className="mr-2 h-4 w-4" />
                  Browse Files
                </span>
              </Button>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                className="hidden"
              />
            </label>
          </div>

          <div className="flex justify-center">
            <Button variant="ghost" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Download CSV Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
